import React from 'react'
import {
    Box,
    InputBase,
    List,
    ListItem,
    Chip,
    Typography,
    Tooltip,
    IconButton,
    Paper,
    ClickAwayListener,
    Divider,
} from '@mui/material'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faExclamationCircle,
    faSearch,
} from '@fortawesome/pro-duotone-svg-icons'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import { useTheme } from '@mui/material/styles'

import { isAllowed, shortenText, formatDate } from '../model/quicksearch.utils'
import { useQuickSearch } from '../hooks/useQuickSearch'

export const UpperMenuQuickSearch: React.FC<any> = ({ kedo }) => {
    const theme = useTheme()
    const {
        items,
        show,
        search,
        showSearch,
        loading,
        onChange,
        onKeyUp,
        setShowSearch,
        setItems,
        setSearch,
        setShow,
        handleClickAway,
    } = useQuickSearch(kedo)

    const getDefDossierName = (
        item: any,
        defDossiers: any[],
        selectedLocale: any
    ) => {
        if (!selectedLocale) return null
        const defDossier = defDossiers.find(
            (dd) => dd.id === item.def_dossier_id
        )
        const entity = kedo.env().getDefDossierById(item.def_dossier_id)
        const tr = kedo.env().translateItem(entity, 'defdossier', true)
        if (!tr) return null

        return (
            <Link
                onClick={() => {
                    setItems([])
                    setSearch('')
                    setShow(false)
                }}
                to={`/defdossier/${item.def_dossier_id}`}
            >
                <Chip size="small" label={tr} />
            </Link>
        )
    }

    const renderItems = () => {
        const defDossiers =
            items.length > 0
                ? kedo.env().getCurrentEnvironmentDefDossiers()
                : []
        const selectedLocale =
            items.length > 0 ? kedo.env().getSelectedLocale() : null

        if (loading) {
            return (
                <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    <ListItem>
                        <LoadingDefault as="span" size="sm" />
                        {kedo.t('Searching for items...')}
                    </ListItem>
                </List>
            )
        }

        if (items.length > 0) {
            return (
                <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {items.map((item) => (
                        <>
                            <ListItem
                                key={item.id}
                                button
                                component={Link}
                                to={`/contentdossier/${item.id}`}
                                onClick={() => {
                                    setItems([])
                                    setSearch('')
                                    setShow(false)
                                    setShowSearch(false)
                                }}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color={theme.palette.action.active}
                                    sx={{ mb: 0.5 }}
                                >
                                    {shortenText(item.summary)}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mr: 1,
                                        mb: 1,
                                    }}
                                >
                                    {getDefDossierName(
                                        item,
                                        defDossiers,
                                        selectedLocale
                                    )}
                                    {item.created_at && (
                                        <Typography variant="caption">
                                            {formatDate(item.created_at)}
                                        </Typography>
                                    )}
                                </Box>
                            </ListItem>
                            <Divider variant="middle" />
                        </>
                    ))}
                </List>
            )
        }

        return (
            <List>
                <ListItem>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                    &nbsp; {kedo.t('No items found')}
                </ListItem>
            </List>
        )
    }

    return (
        <>
            {isAllowed(kedo, 'quick_search') && (
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        {!showSearch ? (
                            <Tooltip title={kedo.t('Quick Search')}>
                                <IconButton
                                    color="inherit"
                                    size="large"
                                    onClick={() => setShowSearch(true)}
                                >
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        style={{ fontSize: 18 }}
                                    />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: 'background.paper',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        boxShadow: 1,
                                        width: '360px',
                                    }}
                                >
                                    <IconButton size="small" disabled>
                                        <FontAwesomeIcon icon={faSearch} />
                                    </IconButton>

                                    <InputBase
                                        placeholder={
                                            kedo.t('Quick Search') + '...'
                                        }
                                        name="search"
                                        value={search}
                                        onChange={onChange}
                                        onKeyUp={onKeyUp}
                                        sx={{ ml: 1, flex: 1 }}
                                    />
                                </Box>

                                {show && (
                                    <Paper
                                        sx={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            zIndex: 10,
                                            mt: 0.5,
                                            width: '100%',
                                        }}
                                    >
                                        {renderItems()}
                                    </Paper>
                                )}
                            </>
                        )}
                    </Box>
                </ClickAwayListener>
            )}
        </>
    )
}
